import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormLabel } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { X } from "lucide-react";
import { format, parseISO } from "date-fns";
import { WorkDetailsForm } from "../../WorkDetailsForm";
import type { WorkDetails } from "../../WorkDetailsForm";
import type { OrderLine, ProductOption, Configuration } from "../types";

interface OrderLineRowProps {
  line: OrderLine;
  products: ProductOption[] | undefined;
  configuration: Configuration | null;
  configurationLineId: string | null;
  orderType: "new_work" | "pickup_work" | "field_work";
  editingPrices: Record<string, string>;
  onUpdateLine: (id: string, field: keyof OrderLine, value: string | number | boolean | null | Record<string, string> | WorkDetails) => void;
  onSelectProduct: (lineId: string, productId: string) => void;
  onRemoveLine: (id: string) => void;
  onEditingPriceChange: (lineId: string, value: string | undefined) => void;
  getDisplayPrice: (line: OrderLine) => number;
  getAlternativePrice: (line: OrderLine) => { label: string; value: number };
}

export function OrderLineRow({
  line,
  products,
  configuration,
  configurationLineId,
  orderType,
  editingPrices,
  onUpdateLine,
  onSelectProduct,
  onRemoveLine,
  onEditingPriceChange,
  getDisplayPrice,
  getAlternativePrice,
}: OrderLineRowProps) {
  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1 space-y-2">
          <div>
            <FormLabel>Tuote</FormLabel>
            <Select value={line.product_id || "none"} onValueChange={(value) => onSelectProduct(line.id, value)}>
              <SelectTrigger><SelectValue placeholder="Valitse tuote (valinnainen)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Ei tuotetta</SelectItem>
                {products?.map((product) => (
                  <SelectItem key={product.id} value={product.id}>
                    <div className="flex items-center gap-2">
                      {product.image_url ? <img src={product.image_url} alt={product.name} className="h-8 w-8 rounded object-cover" /> : <div className="h-8 w-8 rounded bg-muted flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{product.name}</div>
                        <div className="text-sm text-muted-foreground">{product.price.toFixed(2)} € {product.category && `• ${product.category}`}</div>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <FormLabel>Kuvaus</FormLabel>
            <Input placeholder="Kuvaus *" value={line.description} onChange={(e) => onUpdateLine(line.id, "description", e.target.value)} />
          </div>
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={() => onRemoveLine(line.id)}><X className="h-4 w-4" /></Button>
      </div>

      <div className="mb-2">
        <FormLabel className="text-xs text-muted-foreground">Hinnan syöttötapa</FormLabel>
        <RadioGroup value={line.price_includes_vat ? "gross" : "net"} onValueChange={(value) => onUpdateLine(line.id, "price_includes_vat", value === "gross")} className="flex gap-4 mt-1">
          <div className="flex items-center space-x-2"><RadioGroupItem value="net" id={`net-${line.id}`} /><FormLabel htmlFor={`net-${line.id}`} className="font-normal cursor-pointer text-sm">Veroton</FormLabel></div>
          <div className="flex items-center space-x-2"><RadioGroupItem value="gross" id={`gross-${line.id}`} /><FormLabel htmlFor={`gross-${line.id}`} className="font-normal cursor-pointer text-sm">Verollinen</FormLabel></div>
        </RadioGroup>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div><FormLabel>Määrä</FormLabel><Input type="number" min="0" step="0.01" value={line.quantity} onChange={(e) => onUpdateLine(line.id, "quantity", parseFloat(e.target.value) || 0)} /></div>
        <div>
          <FormLabel>Yksikköhinta ({line.price_includes_vat ? "verollinen" : "veroton"}) €</FormLabel>
          <Input type="number" min="0" step="0.01" value={editingPrices[line.id] !== undefined ? editingPrices[line.id] : getDisplayPrice(line).toFixed(2)} onFocus={() => onEditingPriceChange(line.id, getDisplayPrice(line).toFixed(2))} onChange={(e) => onEditingPriceChange(line.id, e.target.value)} onBlur={() => { onUpdateLine(line.id, "unit_price", parseFloat(editingPrices[line.id]) || 0); onEditingPriceChange(line.id, undefined); }} />
          <p className="text-xs text-muted-foreground mt-1">{getAlternativePrice(line).label}: {getAlternativePrice(line).value.toFixed(2)} €</p>
        </div>
        <div><FormLabel>ALV %</FormLabel><Input type="number" min="0" max="100" step="0.5" value={line.vat_rate} onChange={(e) => onUpdateLine(line.id, "vat_rate", parseFloat(e.target.value) || 0)} /></div>
        <div><FormLabel>Yhteensä (veroton) €</FormLabel><Input type="text" value={line.line_total.toFixed(2)} disabled /><p className="text-xs text-muted-foreground mt-1">Verollinen: {(line.line_total * (1 + line.vat_rate / 100)).toFixed(2)} €</p></div>
      </div>

      {orderType === "pickup_work" && (
        <div className="mt-3"><WorkDetailsForm value={line.work_details || null} onChange={(workDetails) => onUpdateLine(line.id, "work_details", workDetails)} /></div>
      )}

      {line.id === configurationLineId && configuration && (
        <div className="mt-3 pt-3 border-t bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2"><span className="text-sm font-medium text-muted-foreground">Konfiguraatio:</span><span className="text-sm font-semibold">{configuration.productModelName}</span></div>
          <div className="space-y-2">
            {(() => {
              const activeComponents = configuration.components.filter(c => c.quantity > 0);
              const groups = new Map<string, typeof activeComponents>();
              for (const comp of activeComponents) {
                const key = comp.sourceModelName || "";
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key)!.push(comp);
              }
              return Array.from(groups.entries()).map(([modelName, components]) => (
                <div key={modelName || "__ungrouped"} className="space-y-1">
                  {modelName && (
                    <div className="text-xs font-semibold text-blue-700 bg-blue-50 rounded px-2 py-0.5 inline-block">
                      {modelName}
                    </div>
                  )}
                  <div className={modelName ? "ml-3 space-y-1" : "space-y-1"}>
                    {components.map((component) => {
                      const selectedMaterial = component.allowedMaterials.find(m => m.id === component.materialId);
                      const filledAttributes = component.attributes.filter(attr => attr.value !== null && attr.value !== "");
                      return (
                        <div key={component.id} className="text-sm bg-background rounded p-2">
                          <div className="flex items-center gap-2"><span className="font-medium">{component.componentTypeLabel}</span>{component.quantity > 1 && <span className="text-muted-foreground">× {component.quantity}</span>}</div>
                          <div className="ml-2 mt-1 space-y-0.5 text-muted-foreground">
                            {selectedMaterial && <div className="flex items-center gap-1"><span className="text-xs">Materiaali:</span><span className="text-xs font-medium text-foreground">{selectedMaterial.name}</span></div>}
                            {filledAttributes.map((attr) => (<div key={attr.id} className="flex items-center gap-1"><span className="text-xs">{attr.label}:</span><span className="text-xs font-medium text-foreground">{attr.type === "date" && attr.value ? format(parseISO(String(attr.value)), "dd.MM.yyyy") : String(attr.value)}</span></div>))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
